import React from 'react';
import { Link } from '@/components/LocalizedLink';
import { ArrowRight, FileDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { fadeUp } from '@/lib/motion';
import { useLanguage } from '@/hooks/useLanguage';

const formatValue = (value, sep) => (Array.isArray(value) ? value.join(sep) : value);

function KeySpecs({ specs }) {
  if (specs.length === 0) return null;
  return (
    <div className="mt-8 grid grid-cols-2 gap-4" data-testid="key-specs-grid">
      {specs.map(item => (
        <div key={item.label} className="flex items-start gap-3 bg-saffron-50/40 p-4 rounded-lg" data-testid={`spec-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
          <item.icon size={18} className="text-saffron flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[10px] uppercase tracking-wider text-saffron font-sans font-semibold">{item.label}</p>
            <p className="text-sm text-earth-dark font-sans font-medium mt-0.5">{formatValue(item.value, ', ')}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function ProductActions({ product }) {
  const { t } = useLanguage();
  const whatsappText = `Hi%2C%20I%27m%20interested%20in%20${encodeURIComponent(product.name)}.%20Please%20share%20pricing%20and%20availability.`;
  return (
    <div className="mt-8 flex flex-wrap gap-3">
      <Link to={`/contact?product=${encodeURIComponent(product.name)}`} className="inline-flex items-center gap-2 bg-saffron text-white font-sans font-semibold text-sm px-8 py-3.5 rounded-md hover:bg-saffron-dark transition-colors shadow-saffron-glow" data-testid="request-quote-button">
        {t.productDetail.requestQuote} <ArrowRight size={16} />
      </Link>
      <a href={`https://wa.me/919284548218?text=${whatsappText}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-earth/20 text-earth-dark font-sans font-semibold text-sm px-8 py-3.5 rounded-md hover:bg-earth/5 transition-colors" data-testid="whatsapp-inquiry">
        {t.productDetail.whatsappInquiry}
      </a>
      {product.brochure_url && (
        <a href={product.brochure_url} download target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-saffron/30 text-saffron font-sans font-semibold text-sm px-6 py-3.5 rounded-md hover:bg-saffron/5 transition-colors" data-testid="download-brochure">
          <FileDown size={16} /> {t.productDetail.downloadBrochure}
        </a>
      )}
    </div>
  );
}

export default function ProductSummary({ product, keySpecs }) {
  return (
    <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ delay: 0.2 }}>
      <div className="w-10 h-10 rounded-full mb-4" style={{ backgroundColor: product.color + '30', border: `2px solid ${product.color}` }}>
        <div className="w-full h-full rounded-full" style={{ backgroundColor: product.color, opacity: 0.5 }} />
      </div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-earth-dark tracking-tight" data-testid="product-name">{product.name}</h1>
      <p className="text-saffron font-sans font-medium mt-2">{product.tagline}</p>
      <p className="mt-6 text-earth/60 font-sans leading-relaxed" data-testid="product-description">{product.description}</p>
      <KeySpecs specs={keySpecs} />
      <ProductActions product={product} />
    </motion.div>
  );
}

export { formatValue };
